/**
 * Error Handler
 *
 * Provides centralized error handling utilities including global error capture,
 * error normalization, and user-friendly message generation.
 */

import {
  AppError,
  AuthenticationError,
  AuthorizationError,
  ConflictError,
  ErrorCode,
  ErrorSeverity,
  NetworkError,
  NotFoundError,
  ValidationError,
  isAppError,
  isAuthenticationError,
  isAuthorizationError,
  isNetworkError,
  isValidationError,
  isNotFoundError,
  isConflictError,
} from './errorTypes';

// =============================================================================
// ENVIRONMENT DETECTION
// =============================================================================

/**
 * Determines if we are running in development mode.
 */
function isDevelopment(): boolean {
  if (typeof process !== 'undefined' && process.env) {
    return process.env.NODE_ENV === 'development';
  }
  // Browser environment fallback
  if (typeof window !== 'undefined') {
    return (
      window.location.hostname === 'localhost' ||
      window.location.hostname === '127.0.0.1'
    );
  }
  return false;
}

// =============================================================================
// ERROR INFO INTERFACE
// =============================================================================

/**
 * Normalized error information for consistent handling.
 */
export interface ErrorInfo {
  /** User-friendly error message in Italian */
  message: string;
  /** Error code for programmatic handling */
  code: ErrorCode;
  /** Error severity level */
  severity: ErrorSeverity;
  /** Whether the error is recoverable */
  isRecoverable: boolean;
  /** Original error (if available) */
  originalError: Error | null;
  /** Stack trace (only in development) */
  stack?: string;
  /** Additional context metadata */
  metadata: Record<string, unknown>;
  /** Timestamp of when the error occurred */
  timestamp: Date;
}

// =============================================================================
// ERROR MESSAGES (ITALIAN)
// =============================================================================

/**
 * Default user-friendly error messages in Italian.
 */
const DEFAULT_MESSAGES: Record<ErrorCode, string> = {
  [ErrorCode.UNKNOWN]: 'Si è verificato un errore imprevisto.',
  [ErrorCode.INTERNAL]: 'Errore interno del sistema.',
  [ErrorCode.VALIDATION_FAILED]: 'I dati inseriti non sono validi.',
  [ErrorCode.INVALID_INPUT]: 'I dati inseriti non sono nel formato corretto.',
  [ErrorCode.MISSING_REQUIRED_FIELD]: 'Compila tutti i campi obbligatori.',
  [ErrorCode.AUTHENTICATION_FAILED]: 'Autenticazione non riuscita.',
  [ErrorCode.SESSION_EXPIRED]: 'La sessione è scaduta. Effettua nuovamente il login.',
  [ErrorCode.INVALID_CREDENTIALS]: 'Email o password non corretti.',
  [ErrorCode.TOKEN_EXPIRED]: 'Il token di autenticazione è scaduto.',
  [ErrorCode.TOKEN_INVALID]: 'Token di autenticazione non valido.',
  [ErrorCode.AUTHORIZATION_FAILED]: 'Non sei autorizzato a eseguire questa operazione.',
  [ErrorCode.INSUFFICIENT_PERMISSIONS]: 'Non hai i permessi necessari.',
  [ErrorCode.ACCESS_DENIED]: 'Accesso negato.',
  [ErrorCode.NETWORK_ERROR]: 'Errore di connessione. Verifica la tua connessione internet.',
  [ErrorCode.TIMEOUT]: 'La richiesta ha impiegato troppo tempo. Riprova.',
  [ErrorCode.CONNECTION_REFUSED]: 'Impossibile connettersi al server.',
  [ErrorCode.SERVICE_UNAVAILABLE]: 'Il servizio non è attualmente disponibile.',
  [ErrorCode.NOT_FOUND]: 'La risorsa richiesta non è stata trovata.',
  [ErrorCode.RESOURCE_NOT_FOUND]: 'Elemento non trovato.',
  [ErrorCode.CONFLICT]: 'Conflitto durante l\'operazione.',
  [ErrorCode.DUPLICATE_ENTRY]: 'Esiste già un elemento con questi dati.',
  [ErrorCode.CONCURRENT_MODIFICATION]:
    'I dati sono stati modificati da un altro utente. Ricarica la pagina.',
};

// =============================================================================
// ERROR DETECTION UTILITIES
// =============================================================================

/**
 * Checks if an error is network-related.
 */
export function isNetworkRelatedError(error: unknown): boolean {
  if (isNetworkError(error)) {
    return true;
  }

  if (error instanceof Error) {
    const message = error.message.toLowerCase();
    const networkIndicators = [
      'network',
      'fetch',
      'xhr',
      'timeout',
      'econnrefused',
      'enotfound',
      'socket',
      'connection',
      'offline',
      'internet',
    ];

    return networkIndicators.some((indicator) => message.includes(indicator));
  }

  // Check for fetch API errors
  if (error instanceof TypeError) {
    return error.message.toLowerCase().includes('fetch');
  }

  return false;
}

/**
 * Checks if an error is authentication-related.
 */
export function isAuthError(error: unknown): boolean {
  if (isAuthenticationError(error) || isAuthorizationError(error)) {
    return true;
  }

  if (isNetworkError(error)) {
    return error.statusCode === 401 || error.statusCode === 403;
  }

  if (isAppError(error)) {
    const authCodes = [
      ErrorCode.AUTHENTICATION_FAILED,
      ErrorCode.SESSION_EXPIRED,
      ErrorCode.INVALID_CREDENTIALS,
      ErrorCode.TOKEN_EXPIRED,
      ErrorCode.TOKEN_INVALID,
      ErrorCode.AUTHORIZATION_FAILED,
      ErrorCode.INSUFFICIENT_PERMISSIONS,
      ErrorCode.ACCESS_DENIED,
    ];
    return authCodes.includes(error.code);
  }

  return false;
}

// =============================================================================
// ERROR NORMALIZATION
// =============================================================================

/**
 * Normalizes any error into a consistent ErrorInfo structure.
 */
export function handleError(error: unknown): ErrorInfo {
  const timestamp = new Date();

  // Handle AppError and its subclasses
  if (isAppError(error)) {
    return {
      message: error.message,
      code: error.code,
      severity: error.severity,
      isRecoverable: error.isRecoverable,
      originalError: error,
      stack: isDevelopment() ? error.stack : undefined,
      metadata: error.metadata,
      timestamp,
    };
  }

  // Handle standard Error objects
  if (error instanceof Error) {
    // Detect network errors from standard errors
    if (isNetworkRelatedError(error)) {
      return {
        message: DEFAULT_MESSAGES[ErrorCode.NETWORK_ERROR],
        code: ErrorCode.NETWORK_ERROR,
        severity: ErrorSeverity.MEDIUM,
        isRecoverable: true,
        originalError: error,
        stack: isDevelopment() ? error.stack : undefined,
        metadata: { originalMessage: error.message },
        timestamp,
      };
    }

    // Generic error handling
    return {
      message: isDevelopment() ? error.message : DEFAULT_MESSAGES[ErrorCode.UNKNOWN],
      code: ErrorCode.UNKNOWN,
      severity: ErrorSeverity.MEDIUM,
      isRecoverable: true,
      originalError: error,
      stack: isDevelopment() ? error.stack : undefined,
      metadata: { originalMessage: error.message },
      timestamp,
    };
  }

  // Handle string errors
  if (typeof error === 'string') {
    return {
      message: isDevelopment() ? error : DEFAULT_MESSAGES[ErrorCode.UNKNOWN],
      code: ErrorCode.UNKNOWN,
      severity: ErrorSeverity.MEDIUM,
      isRecoverable: true,
      originalError: new Error(error),
      metadata: { originalMessage: error },
      timestamp,
    };
  }

  // Handle unknown error types
  return {
    message: DEFAULT_MESSAGES[ErrorCode.UNKNOWN],
    code: ErrorCode.UNKNOWN,
    severity: ErrorSeverity.MEDIUM,
    isRecoverable: true,
    originalError: null,
    metadata: { rawError: error },
    timestamp,
  };
}

// =============================================================================
// USER-FRIENDLY MESSAGES
// =============================================================================

/**
 * Gets a user-friendly error message in Italian.
 */
export function getErrorMessage(error: unknown): string {
  // Handle AppError and subclasses
  if (isAppError(error)) {
    // Use the error's message if it's meaningful
    if (error.message && error.message.length > 0) {
      return error.message;
    }
    // Fall back to default message for the error code
    return DEFAULT_MESSAGES[error.code] ?? DEFAULT_MESSAGES[ErrorCode.UNKNOWN];
  }

  // Handle validation errors with field information
  if (isValidationError(error) && error.validationErrors.length > 0) {
    if (error.validationErrors.length === 1) {
      return error.validationErrors[0].message;
    }
    return `Errori di validazione: ${error.validationErrors.map((e) => e.message).join(', ')}`;
  }

  // Handle network-related errors
  if (isNetworkRelatedError(error)) {
    return DEFAULT_MESSAGES[ErrorCode.NETWORK_ERROR];
  }

  // Handle standard Error objects
  if (error instanceof Error) {
    // In development, show the actual message
    if (isDevelopment()) {
      return error.message;
    }
    // In production, show a generic message
    return DEFAULT_MESSAGES[ErrorCode.UNKNOWN];
  }

  // Handle string errors
  if (typeof error === 'string') {
    return isDevelopment() ? error : DEFAULT_MESSAGES[ErrorCode.UNKNOWN];
  }

  // Default fallback
  return DEFAULT_MESSAGES[ErrorCode.UNKNOWN];
}

/**
 * Gets an error message with specific context.
 */
export function getContextualErrorMessage(error: unknown, context: string): string {
  const baseMessage = getErrorMessage(error);

  if (isNotFoundError(error)) {
    return `${error.resourceType} non trovato durante ${context}.`;
  }

  if (isConflictError(error)) {
    return `Conflitto durante ${context}: ${baseMessage}`;
  }

  return `Errore durante ${context}: ${baseMessage}`;
}

// =============================================================================
// GLOBAL ERROR HANDLER
// =============================================================================

/**
 * Global error handler callback type.
 */
type GlobalErrorCallback = (errorInfo: ErrorInfo) => void;

/**
 * Registered global error handlers.
 */
let globalErrorHandlers: GlobalErrorCallback[] = [];

/**
 * Whether global error handling is registered.
 */
let isGlobalHandlerRegistered = false;

/**
 * Registers global error handlers for unhandled errors and promise rejections.
 */
export function registerGlobalErrorHandler(onError?: GlobalErrorCallback): void {
  if (onError) {
    globalErrorHandlers.push(onError);
  }

  // Avoid registering multiple times
  if (isGlobalHandlerRegistered) {
    return;
  }

  isGlobalHandlerRegistered = true;

  // Handle uncaught errors in browser environment
  if (typeof window !== 'undefined') {
    window.addEventListener('error', (event: ErrorEvent) => {
      const errorInfo = handleError(event.error || new Error(event.message));

      // Log in development
      if (isDevelopment()) {
        console.error('[Global Error Handler] Uncaught error:', event.error);
        console.error('Stack:', errorInfo.stack);
      }

      // Notify all registered handlers
      globalErrorHandlers.forEach((handler) => {
        try {
          handler(errorInfo);
        } catch (handlerError) {
          console.error('[Global Error Handler] Handler threw an error:', handlerError);
        }
      });
    });

    window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
      const errorInfo = handleError(event.reason);

      // Log in development
      if (isDevelopment()) {
        console.error('[Global Error Handler] Unhandled promise rejection:', event.reason);
        console.error('Stack:', errorInfo.stack);
      }

      // Notify all registered handlers
      globalErrorHandlers.forEach((handler) => {
        try {
          handler(errorInfo);
        } catch (handlerError) {
          console.error('[Global Error Handler] Handler threw an error:', handlerError);
        }
      });
    });
  }

  // Handle uncaught errors in Node.js environment
  if (typeof process !== 'undefined' && process.on) {
    process.on('uncaughtException', (error: Error) => {
      const errorInfo = handleError(error);

      if (isDevelopment()) {
        console.error('[Global Error Handler] Uncaught exception:', error);
      }

      globalErrorHandlers.forEach((handler) => {
        try {
          handler(errorInfo);
        } catch (handlerError) {
          console.error('[Global Error Handler] Handler threw an error:', handlerError);
        }
      });
    });

    process.on('unhandledRejection', (reason: unknown) => {
      const errorInfo = handleError(reason);

      if (isDevelopment()) {
        console.error('[Global Error Handler] Unhandled rejection:', reason);
      }

      globalErrorHandlers.forEach((handler) => {
        try {
          handler(errorInfo);
        } catch (handlerError) {
          console.error('[Global Error Handler] Handler threw an error:', handlerError);
        }
      });
    });
  }
}

/**
 * Unregisters a global error handler callback.
 */
export function unregisterGlobalErrorHandler(callback: GlobalErrorCallback): void {
  globalErrorHandlers = globalErrorHandlers.filter((handler) => handler !== callback);
}

/**
 * Clears all registered global error handlers.
 * Useful for testing purposes.
 */
export function clearGlobalErrorHandlers(): void {
  globalErrorHandlers = [];
}

// =============================================================================
// ERROR CREATION UTILITIES
// =============================================================================

/**
 * Creates an appropriate error from an HTTP response.
 */
export function createErrorFromResponse(
  statusCode: number,
  message?: string,
  metadata?: Record<string, unknown>
): AppError {
  switch (statusCode) {
    case 400:
      return new ValidationError(message ?? DEFAULT_MESSAGES[ErrorCode.INVALID_INPUT], {
        metadata,
      });
    case 401:
      return new AuthenticationError(
        message ?? DEFAULT_MESSAGES[ErrorCode.AUTHENTICATION_FAILED],
        { metadata }
      );
    case 403:
      return new AuthorizationError(message ?? DEFAULT_MESSAGES[ErrorCode.ACCESS_DENIED], {
        metadata,
      });
    case 404:
      return new NotFoundError('Risorsa', undefined, { message, metadata });
    case 409:
      return new ConflictError(message ?? DEFAULT_MESSAGES[ErrorCode.CONFLICT], {
        metadata,
      });
    default:
      return NetworkError.fromStatusCode(statusCode);
  }
}

/**
 * Wraps a function to catch and normalize errors.
 */
export function withErrorHandling<T extends (...args: unknown[]) => unknown>(
  fn: T,
  context?: string
): (...args: Parameters<T>) => ReturnType<T> {
  return ((...args: Parameters<T>): ReturnType<T> => {
    try {
      const result = fn(...args);

      // Handle promises
      if (result instanceof Promise) {
        return result.catch((error) => {
          const errorInfo = handleError(error);
          const message = context
            ? getContextualErrorMessage(error, context)
            : errorInfo.message;

          throw new AppError(message, errorInfo.code, {
            severity: errorInfo.severity,
            isRecoverable: errorInfo.isRecoverable,
            metadata: { ...errorInfo.metadata, context },
          });
        }) as ReturnType<T>;
      }

      return result as ReturnType<T>;
    } catch (error) {
      const errorInfo = handleError(error);
      const message = context
        ? getContextualErrorMessage(error, context)
        : errorInfo.message;

      throw new AppError(message, errorInfo.code, {
        severity: errorInfo.severity,
        isRecoverable: errorInfo.isRecoverable,
        metadata: { ...errorInfo.metadata, context },
      });
    }
  }) as (...args: Parameters<T>) => ReturnType<T>;
}
